import type { FC, MouseEvent } from "react";
import { useCallback } from "react";
import type { Doc } from "sharedb/lib/client";

import type { BlockType, FieldType, WorksheetType } from "../../shared/types/worksheet";
import { STATIC_CONTENT_FIELD_TYPES, isQuestionBlock } from "../../shared/worksheets/constants";

import { showTemplateBlockModal } from "./TemplateBlockModal";

import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { Tooltip } from "mds/components/Tooltip";
import { PageIcon } from "mds/icons";
import type { DocViewContextType } from "providers/DocViewProvider";

// eslint-disable-next-line react-refresh/only-export-components
export function getTemplateFieldIndex({
  accessId,
  block,
  canAuthorCourse,
  showResponses,
}: {
  accessId: string;
  block: BlockType | undefined;
  canAuthorCourse: boolean;
  showResponses: DocViewContextType["showResponses"];
}): number {
  if (!block || !accessId || canAuthorCourse || showResponses) {
    return -1;
  }

  // Never show this affordance for static-only blocks.
  if (!isQuestionBlock(block)) {
    return -1;
  }

  const firstNonStaticFieldIndex = (block.f as FieldType[]).findIndex(
    (f) => f && !STATIC_CONTENT_FIELD_TYPES.includes(f.t),
  );
  return firstNonStaticFieldIndex !== -1 ? firstNonStaticFieldIndex : 0;
}

type TemplateBlockButtonProps = {
  block: BlockType;
  doc: Doc<WorksheetType>;
  docViewContextValue: DocViewContextType;
  className?: string;
  disabled?: boolean;
};

export const TemplateBlockButton: FC<TemplateBlockButtonProps> = ({
  block,
  doc,
  docViewContextValue,
  className,
  disabled,
}) => {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      showTemplateBlockModal({ block, doc, docViewContextValue });
    },
    [block, doc, docViewContextValue],
  );

  return (
    <Tooltip content={t("worksheet.template_block_modal.title")}>
      <Button
        aria-label={t("worksheet.template_block_modal.title")}
        className={className}
        disabled={disabled}
        kind="white"
        size="s"
        isSquare
        onClick={handleClick}
      >
        <PageIcon />
      </Button>
    </Tooltip>
  );
};
